import { WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import FormTextField from 'components/form/FormTextField';
import { useEffect, useMemo } from 'react';
import { useVerificationContext } from '../helpers/verificationContext';
import { FormDateField } from 'components/form/FormDateField';
import { useFormikContext } from 'formik';
import { useAfterMountEffect } from '../../../helpers/useAfterMountEffect';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import Button from 'components/button/Button';

import { FormValues, resolveMonthlyVolumeUsdId } from '../schema';

export const RegistrationInformation = () => {
    const { selectOptions } = useVerificationContext();
    const { values, setFieldValue } =
        useFormikContext<FormValues>();
    const companyName = localStorage?.getItem("companyName")
    const mailToDomain = companyName?.toLowerCase()?.replace(/\s+/g, "")

    const availableStates = useMemo(() => {
        return (
            selectOptions?.availableCountries
                ?.find(
                    (country) => country.isoCode === values.registrationInformation.addressCountry
                )
                ?.states?.map((state) => ({
                    label: state.stateProvinceName,
                    value: state.ansiCode,
                })) ?? null
        );
    }, [values.registrationInformation.addressCountry, selectOptions?.availableCountries]);
    const availableCountries = useMemo(
        () =>
            selectOptions?.availableCountries?.map((country) => ({
                label: country.countryName,
                value: country.isoCode,
            })) ?? [],
        [selectOptions?.availableCountries]
    );
    const isOtherEntityType = useMemo(
        () =>
            values.registrationInformation.entityTypesID &&
            values.registrationInformation.entityTypesID ==
                selectOptions?.entityTypes?.find((type) => type.label === 'Other')?.value,
        [values.registrationInformation.entityTypesID, selectOptions?.entityTypes]
    );
    useEffect(() => {
        if (!isOtherEntityType) setFieldValue(fieldName('entityTypesOther'), null);
    }, [isOtherEntityType, setFieldValue]);

    // Reset field when the country changes
    useAfterMountEffect(() => {
        setFieldValue(fieldName('addressState'), '');
    }, [values.registrationInformation.addressCountry]);

    // Migrate legacy monthlyUsdValueId into the V3 UI field before clearing it on country change.
    useAfterMountEffect(() => {
        const volumeId = resolveMonthlyVolumeUsdId(values.operationsInformation);
        if (volumeId != null) {
            setFieldValue('operationsInformation.anticipatedMonthlyVolumeUsdId', volumeId);
        }
        setFieldValue('operationsInformation.monthlyUsdValueId', null);
    }, [values.registrationInformation.addressCountry, setFieldValue]);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <a href={`mailto:onboarding@${mailToDomain}.com`} style={{ textDecoration: 'none' }}>
                    <Button priority="secondary" variety="small" type="button">
                        Contact Us
                    </Button>
                </a>
            </div>
            <h2>Entity Registration Information</h2>
            <h3>Business Registration Information</h3>

            {/* General Business Details */}
            <FormTextField
                field={fieldName('entityName')}
                label="Legal Name of Entity *"
                tooltip={
                    'The name in which the entity was formed, it should be listed on the formation documents.  This name needs to be accurate, please enter the name as it appears on the formation documents (or official name change documents)  to ensure banking parnters process funds.'
                }
            />
            <div className="FormSideBySide">
                <div className="FormBuilderComponent SideBySide">
                    <WrappedFormSingleSelectField
                        fieldName={fieldName('entityTypesID')}
                        options={selectOptions?.entityTypes ?? []}
                        label="Type of Entity *"
                    />
                </div>
                <div className="FormBuilderComponent SideBySide">
                    <FormDateField
                        field={fieldName('registrationDate')}
                        label="Date of Entity Registration *"
                        tooltip="Please enter the date that the entity was registered in it's jurisdiction. Example:  In the US, a new business in Delware will register with Deleware State, please enter the date of that registration. "
                    />
                </div>
            </div>
            {isOtherEntityType && (
                <FormTextField field={fieldName('entityTypesOther')} label="Entity Type" />
            )}
            <div className="FormSideBySide">
                <FormTextField
                    field={fieldName('registrationNumber')}
                    label="Registration Number *"
                    sideBySide
                    tooltip="Please enter the number provided by the government agency when the entity was registered"
                />
                <FormTextField
                    field={fieldName('officialBusinessRegistrationNumber')}
                    label="Official Business Registration Number *"
                    sideBySide
                    tooltip="Please enter the official business registration number"
                />
            </div>
            <FormTextField field={fieldName('taxNumber')} label="Tax ID Number *" type="number" />
            <FormTextField
                type="textarea"
                field={fieldName('doingBusinessAs')}
                label="Doing Business As - DBA (Please list all business names) *"
                tooltip={
                    <>
                        Please enter all names the business uses to operate or advertise. Example:
                        Google operates as "Google" but is owned by ABC. We'd need both names."
                    </>
                }
            />

            {/* Registered Address */}
            <h3>Registered Address</h3>
            <div className="FormSideBySide">
                <FormTextField
                    field={fieldName('addressStreet')}
                    label="Street Address *"
                    sideBySide
                />
                <FormTextField
                    field={fieldName('addressNumber')}
                    label="Unit Number *"
                    sideBySide
                />
            </div>
            <div className="FormSideBySide FormSideBySideThree">
                <FormTextField field={fieldName('addressCity')} label="City *" sideBySide />
                {availableStates ? (
                    <div className="FormBuilderComponent SideBySide">
                        <WrappedFormSingleSelectField
                            fieldName={fieldName('addressState')}
                            options={availableStates}
                            label="State/Province *"
                        />
                    </div>
                ) : (
                    <FormTextField
                        field={fieldName('addressState')}
                        label="State/Province *"
                        sideBySide
                    />
                )}
                <FormTextField
                    field={fieldName('addressPostCode')}
                    label="Postal Code *"
                    sideBySide
                />
            </div>
            <div className="FormSideBySide">
                <div className="FormBuilderComponent SideBySide" style={{ flex: 1 }}>
                    <WrappedFormSingleSelectField
                        fieldName={fieldName('addressCountry')}
                        options={availableCountries}
                        label="Country *"
                    />
                </div>
            </div>
            {/* Licensing Information */}
            <h3>Licensing Information</h3>
            <div className="FormLabel">
                <label>Does your business require a license? *</label>
            </div>
            <RadioButtons
                options={['Yes', 'No']}
                fieldname={operationsFieldName('bLicenseRequired')}
                label=""
            />
            {values.operationsInformation.bLicenseRequired === 'Yes' && (
                <>
                    <FormTextField
                        field={operationsFieldName('primaryRegulator')}
                        label="Primary Regulator - Example: FCA, SEC, OCC, FINMA, MAS *"
                        placeholder="Enter regulator name"
                        tooltip="The name of the main regulatory authority that oversees your company's financial or crypto-related activities."
                    />
                    <FormTextField
                        field={operationsFieldName('licenseNumber')}
                        label="License Number *"
                        placeholder="Enter license number"
                        tooltip="License number issued by your primary regulator"
                    />
                    <FormTextField
                        type="textarea"
                        field={operationsFieldName('additionalLicensingInfo')}
                        label="Additional information regarding registration or licensing"
                        placeholder="Provide additional details"
                        tooltip="Use this space to provide any explantion or clarification regarding the entity's legal status, registrations, jurisdiction of operation etc."
                    />
                </>
            )}
        </>
    );
};

const fieldName = (name: string) => `registrationInformation.${name}`;
const operationsFieldName = (name: string) => `operationsInformation.${name}`;
